/**
 * Subagent spawn row summary (ticket 3525, upstream pin e3e7cc3f, MIT),
 * ported from upstream `components/chat/agentSpawnSummary.ts`.
 *
 * Forge adaptation: forge has no agents-panel model yet (that surface is a
 * later port ticket), so the agent view is a structural subset of upstream's
 * `RuntimeSubagent` and the status classifiers are local copies of upstream's
 * `isActiveSubagentStatus` / `isTerminalSubagentStatus`.
 *
 * @module agentSpawnSummary
 */

export type SpawnAgentKind = "subagent" | "subagent_batch" | "workflow" | "workflow_agent";

export type SpawnAgentStatus =
  | "pending"
  | "running"
  | "waiting"
  | "idle"
  | "completed"
  | "failed"
  | "cancelled"
  | "interrupted";

export interface SpawnAgentView {
  readonly kind: SpawnAgentKind;
  readonly status: SpawnAgentStatus;
}

const TERMINAL_SPAWN_STATUSES = new Set<SpawnAgentStatus>([
  "completed",
  "failed",
  "cancelled",
  "interrupted",
]);

function isTerminalSpawnStatus(status: SpawnAgentStatus): boolean {
  return TERMINAL_SPAWN_STATUSES.has(status);
}

/** Active = the user may still need to care while it runs. Idle is settled-ish
 * but resumable; waiting counts as active because it needs the user. */
function isActiveSpawnStatus(status: SpawnAgentStatus): boolean {
  return status === "pending" || status === "running" || status === "waiting";
}

/** Summarize observed states without treating idle or missing agents as completed. */
// fallow-ignore-next-line complexity -- port of upstream agentSpawnSummary.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function deriveAgentSpawnSummary({
  agents,
  agentCount,
  coordinatorStatus,
}: {
  agents: ReadonlyArray<SpawnAgentView>;
  agentCount: number;
  coordinatorStatus?: SpawnAgentStatus | undefined;
}) {
  const working = agents.filter((agent) => isActiveSpawnStatus(agent.status)).length;
  const failed = agents.filter((agent) => agent.status === "failed").length;
  const idle = agents.filter((agent) => agent.status === "idle").length;
  const stopped = agents.filter(
    (agent) => agent.status === "cancelled" || agent.status === "interrupted",
  ).length;
  const batches = agents.filter((agent) => agent.kind === "subagent_batch").length;
  const individuals = agentCount - batches;
  // Workflow coordinators can keep running between dynamic member launches.
  const live =
    coordinatorStatus !== undefined ? !isTerminalSpawnStatus(coordinatorStatus) : working > 0;
  const subjects = [
    individuals > 0 ? `${individuals} subagent${individuals === 1 ? "" : "s"}` : null,
    batches > 0
      ? `${batches} ${individuals > 0 ? "" : "subagent "}batch${batches === 1 ? "" : "es"}`
      : null,
  ]
    .filter(Boolean)
    .join(" and ");
  const lead = `${batches > 0 ? "Launched" : live ? "Kicked off" : "Ran"} ${subjects || "subagents"}`;

  const status = live
    ? working > 0
      ? `${working} working`
      : "working"
    : coordinatorStatus === "failed"
      ? "Workflow failed"
      : coordinatorStatus === "cancelled" || coordinatorStatus === "interrupted"
        ? "Workflow stopped"
        : failed > 0
          ? `${failed} failed`
          : stopped > 0
            ? `${stopped} stopped`
            : idle > 0
              ? `${idle} idle`
              : coordinatorStatus !== "completed" &&
                  (agents.length === 0 || agents.length < agentCount)
                ? "Status unavailable"
                : "✓ completed";
  const tone = live
    ? "working"
    : failed > 0 || coordinatorStatus === "failed"
      ? "failed"
      : status === "✓ completed"
        ? "completed"
        : "inactive";
  return { live, lead, status, tone };
}
